type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
};

const isProduction = import.meta.env.PROD;

function getLogLevel(): LogLevel {
  const envLevel = (import.meta.env.VITE_LOG_LEVEL || '').toLowerCase() as LogLevel;
  if (envLevel && envLevel in LOG_LEVELS) {
    return envLevel;
  }
  return isProduction ? 'warn' : 'debug';
}

const originalConsole = {
  log: console.log.bind(console),
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

const noop = (..._args: unknown[]): void => {};

const currentLevel = getLogLevel();
const currentLevelNum = LOG_LEVELS[currentLevel];

export const logger = {
  log: currentLevelNum <= LOG_LEVELS.info ? originalConsole.log : noop,
  debug: currentLevelNum <= LOG_LEVELS.debug ? originalConsole.debug : noop,
  info: currentLevelNum <= LOG_LEVELS.info ? originalConsole.info : noop,
  warn: currentLevelNum <= LOG_LEVELS.warn ? originalConsole.warn : noop,
  error: currentLevelNum <= LOG_LEVELS.error ? originalConsole.error : noop,

  group: currentLevelNum <= LOG_LEVELS.debug
    ? (label: string): void => { console.group(label); }
    : (_label: string): void => {},
  groupEnd: currentLevelNum <= LOG_LEVELS.debug
    ? (): void => { console.groupEnd(); }
    : (): void => {},
  table: currentLevelNum <= LOG_LEVELS.debug
    ? (data: unknown): void => { console.table(data); }
    : (_data: unknown): void => {},
};

export function forceLog(...args: unknown[]): void {
  originalConsole.log(...args);
}

export function forceError(...args: unknown[]): void {
  originalConsole.error(...args);
}

export function initFrontendLogging(): void {
  if (currentLevelNum > LOG_LEVELS.debug) {
    console.debug = noop as typeof console.debug;
  }
  if (currentLevelNum > LOG_LEVELS.info) {
    console.log = noop as typeof console.log;
    console.info = noop as typeof console.info;
  }
  if (currentLevelNum > LOG_LEVELS.warn) {
    console.warn = noop as typeof console.warn;
  }
  if (currentLevelNum > LOG_LEVELS.error) {
    console.error = noop as typeof console.error;
  }

  if (!isProduction) {
    originalConsole.info(`[FrontendLogger] Mode: development - LOG_LEVEL=${currentLevel}`);
  } else {
    originalConsole.info(`[FrontendLogger] Mode: production - LOG_LEVEL=${currentLevel} (active: ${Object.keys(LOG_LEVELS).filter(l => LOG_LEVELS[l as LogLevel] >= currentLevelNum && l !== 'silent').join(', ') || 'none'})`);
  }
}

export { originalConsole };

export const devLog = logger.log;
export const devWarn = logger.warn;
export const devError = logger.error;
